import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useData } from "../context/DataContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Alert, AlertDescription, AlertTitle } from "../components/ui/alert";
import { toast } from "sonner";
import {
  ArrowLeft,
  Calendar,
  User,
  FileText,
  PenTool,
  Check,
  Printer,
  Eye,
  Signature,
  Info,
  Image as ImageIcon,
  X as XIcon, // Import X icon for the viewer close button
  Loader2,
} from "lucide-react";
import { Memo, MemoAssignment } from "../types";
import { toSydneyDate, formatTimestamp } from "../utils/helpers";

// Helper function to get a specific memo assignment
const getMemoAssignment = async (id: any) => {
  const token = localStorage.getItem("token");
  const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || "http://localhost:5001"}/api/memos/${id}/assignment`, {
    method: "GET",
    headers: {
      "Authorization": `Bearer ${token}`,
      "Content-Type": "application/json",
    }
  }  );
  const data = await response.json();
  return data.assignment || null;
}

const ImageViewer = ({ src, alt, onClose }: { src: string; alt: string; onClose: () => void }) => {
  if (!src) return null;

  return (
    <div 
      className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-80 backdrop-blur-sm z-[9997]"
      onClick={onClose}
    >
      <button 
        className="absolute top-4 right-4 text-white hover:text-gray-300 transition-opacity z-[9999]"
        onClick={onClose}
      >
        <XIcon size={32} />
      </button>
      <div className="relative w-[95vw] h-[95vh] md:w-[90vw] md:h-[90vh]" onClick={(e) => e.stopPropagation()}>
        <img src={src} alt={alt} className="w-full h-full object-contain rounded-lg shadow-2xl" />
      </div>
    </div>
  );
};

export default function MemoSignPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const {
    markMemoAsRead,
    signMemo,
    getUserName,
    refreshUserAssignments
  } = useData();

  const [memo, setMemo] = useState<Memo | null>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [signatureType, setSignatureType] = useState<'Draw' | 'Type'>('Type');
  const [typedSignature, setTypedSignature] = useState('');
  const [showSignatureDialog, setShowSignatureDialog] = useState(false);
  const [signedUrls, setSignedUrls] = useState<{ [key: string]: string }>({});
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const hasRefreshedAssignments = useRef(false);
  const [signature, setSignature] = useState<{ signatureType?: string; signatureData?: string; signedDate?: string; ipAddress?: string; userAgent?: string; } | null>(null);
  const [isSigning, setIsSigning] = useState(false);
  const [isMarkingRead, setIsMarkingRead] = useState(false);
  const [isRead, setIsRead] = useState(false);
  const [isSigned, setIsSigned] = useState(false);
  const [assignment, setAssignment] = useState<MemoAssignment | null>(null);

  // State for the image viewer
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [selectedImageAlt, setSelectedImageAlt] = useState<string>('');

  useEffect(() => {
    const fetchMemoAndAssignment = async (memoId: string) => {
      if (!currentUser) return;
      try {
        const assignmentData = await getMemoAssignment(memoId);
        if (assignmentData) {
          setAssignment(assignmentData);
          setSignature({
            signatureType: assignmentData.signatureType,
            signatureData: assignmentData.signatureData,
            signedDate: assignmentData.signedDate,
            ipAddress: assignmentData.ipAddress,
            userAgent: assignmentData.userAgent
          });
          setIsRead(!!assignmentData.readDate);
          setIsSigned(assignmentData.memo.requireSignature && !!assignmentData.signatureData);

          if (assignmentData.memo) {
            setMemo(assignmentData.memo);
            // Fetch signed URLs for image attachments
            const imageAttachments = assignmentData.memo.attachments?.filter(
              (att: any) => att.mimetype.startsWith('image/')
            ) || [];
            
            for (const attachment of imageAttachments) {
              getSignedUrl(attachment.id);
            }
          } else {
            toast.error('Failed to load the associated memo.');
            navigate('/my-policies');
          }
        } else {
          toast.error("You don't have an assignment for this memo.");
          navigate('/my-policies');
        }
      } catch (error) {
        toast.error("Failed to load memo assignment.");
        navigate('/my-policies');
      }
    };
    if (id) {
      fetchMemoAndAssignment(id);
    }
  }, [id, navigate, currentUser]);

  useEffect(() => {
    if (currentUser && !hasRefreshedAssignments.current) {
      refreshUserAssignments();
      hasRefreshedAssignments.current = true;
    }
  }, [currentUser, refreshUserAssignments]);

  // No local formatDate needed, using toSydneyDate and formatTimestamp from helpers
  const sanitizeCaption = (caption: string) => caption ? caption.replace(/<[^>]*>/g, '') : '';

  const getSignedUrl = async (attachmentId: string) => {
    try {
      const API_BASE = process.env.REACT_APP_API_BASE_URL || "http://localhost:5001";
      const response = await fetch(`${API_BASE}/api/memos/${id}/attachments/${attachmentId}/preview`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token'  )}` }
      });
      const data = await response.json();
      if (response.ok && data.success && data.previewUrl) {
        setSignedUrls(prev => ({ ...prev, [attachmentId]: data.previewUrl }));
      } else {
        throw new Error(data.message || 'Failed to get signed URL');
      }
    } catch (error) {
      console.error('Error getting signed URL:', error);
      toast.error('Failed to load image preview');
    }
  };

  const handlePrint = () => window.print();

  const getAssignmentStatus = () => {
    if (!memo || !assignment) return 'New';
    if (memo.requireSignature) return isSigned ? 'Signed' : 'Assigned';
    return isRead ? 'Read' : 'Assigned';
  };

  const getStatusBadgeColor = (status: string) => ({
    'Signed': 'bg-green-200 text-green-600',
    'Read': 'bg-blue-200 text-blue-600',
    'Assigned': 'bg-orange-200 text-orange-600',
    'New': 'bg-red-200 text-red-600',
  }[status] || 'bg-gray-200 text-gray-600');

  const startDrawing = (e: React.MouseEvent<HTMLCanvasElement>) => {
    setIsDrawing(true);
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const rect = canvas.getBoundingClientRect();
    ctx.beginPath();
    ctx.moveTo(e.clientX - rect.left, e.clientY - rect.top);
  };

  const draw = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const rect = canvas.getBoundingClientRect();
    ctx.lineTo(e.clientX - rect.left, e.clientY - rect.top);
    ctx.stroke();
  };

  const stopDrawing = () => setIsDrawing(false);
  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
  };

  const handleSignature = async () => {
    if (!memo || !currentUser || isSigning) return;
    setIsSigning(true);
    try {
      let signatureData = '';
      if (signatureType === 'Type') {
        if (!typedSignature.trim()) {
          toast.error('Please enter your signature');
          return;
        }
        signatureData = typedSignature.trim();
      } else {
        const canvas = canvasRef.current;
        if (!canvas) {
          toast.error('Canvas not available');
          return;
        }
        signatureData = canvas.toDataURL();
      }
      await signMemo(memo.id, { signatureType, signatureData });
      await refreshUserAssignments();
      
      if (id) {
        const assignmentData = await getMemoAssignment(id);
        if (assignmentData) {
            setMemo(assignmentData.memo);
            setAssignment(assignmentData);
            setSignature({
              signatureType: assignmentData?.signatureType,
              signatureData: assignmentData?.signatureData,
              signedDate: assignmentData?.signedDate,
              ipAddress: assignmentData?.ipAddress,
              userAgent: assignmentData?.userAgent
            });
            setIsSigned(true);
        }
      }

      setShowSignatureDialog(false);
      setTypedSignature('');
      clearCanvas();
      toast.success('Memo signed successfully!');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      if (errorMessage.includes('already signed')) {
        setShowSignatureDialog(false);
        setTypedSignature('');
        clearCanvas();
        toast.success('You have already signed this memo');
        await refreshUserAssignments();
        
        if (id) {
          const assignmentData = await getMemoAssignment(id);
          if (assignmentData) {
            setMemo(assignmentData.memo);
            setAssignment(assignmentData);
            setSignature({
              signatureType: assignmentData?.signatureType,
              signatureData: assignmentData?.signatureData,
              signedDate: assignmentData?.signedDate,
              ipAddress: assignmentData?.ipAddress,
              userAgent: assignmentData?.userAgent
            });
            setIsSigned(true);
          }
        }
      } else {
        toast.error('Failed to sign memo');
      }
    } finally {
      setIsSigning(false);
    }
  };

  const handleMarkAsRead = async () => {
    if (!memo || !currentUser || isMarkingRead) return;
    setIsMarkingRead(true);
    try {
      await markMemoAsRead(memo.id, currentUser.id);
      await refreshUserAssignments();

      if (id) {
        const assignmentData = await getMemoAssignment(id);
        if (assignmentData) {
            setAssignment(assignmentData);
            setIsRead(true);
        }
      }

      toast.success('Memo marked as read!');
    } catch (error) {
      toast.error('Failed to mark memo as read');
    } finally {
      setIsMarkingRead(false);
    }
  };

  const handleTouchStart = (e: React.TouchEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const touch = e.touches[0];
    const rect = canvas.getBoundingClientRect();
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    setIsDrawing(true);
    ctx.beginPath();
    ctx.moveTo((touch.clientX - rect.left) * scaleX, (touch.clientY - rect.top) * scaleY);
  };

  const handleTouchMove = (e: React.TouchEvent<HTMLCanvasElement>) => {
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const touch = e.touches[0];
    const rect = canvas.getBoundingClientRect();
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    ctx.lineTo((touch.clientX - rect.left) * scaleX, (touch.clientY - rect.top) * scaleY);
    ctx.stroke();
  };

  if (!memo || !currentUser) {
    return (
      <div className="p-4 md:p-6"><Card><CardContent className="p-6 text-center"><FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" /><h3 className="mb-2">Loading Memo...</h3><p className="text-muted-foreground">Please wait while we load your assignment.</p></CardContent></Card></div>
    );
  }

  const imageAttachments = memo.attachments?.filter(att => att.mimetype.startsWith('image/')) || [];
  const otherAttachments = memo.attachments?.filter(att => !att.mimetype.startsWith('image/')) || [];

  return (
    <>
      <ImageViewer 
        src={selectedImage || ''}
        alt={selectedImageAlt}
        onClose={() => setSelectedImage(null)}
      />
      <div className="p-4 md:p-6 space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex-1">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
              <h1 className="font-bold text-2xl leading-none">{memo.subject}</h1>
              <div className="flex items-center gap-2">
                <Badge variant="default">Memo</Badge>
                {isSigned && <Badge className="bg-green-500"><Check className="h-3 w-3 mr-1" />Signed</Badge>}
                {memo.requireSignature && !isSigned && <Badge variant="secondary">Pending Signature</Badge>}
                {isRead && !memo.requireSignature && <Badge className="bg-green-500"><Check className="h-3 w-3 mr-1" />Read</Badge>}
                {!memo.requireSignature && !isRead && <Badge variant="secondary">Unread</Badge>}
              </div>
            </div>
            <p className="text-muted-foreground mt-1 text-sm">Version {memo.version} • Created {toSydneyDate(memo.createdAt)}</p>
          </div>
          <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => navigate(-1)}><ArrowLeft className="h-4 w-4 mr-2" />Back</Button>
              <Button variant="outline" size="sm" onClick={handlePrint}><Printer className="h-4 w-4 mr-2" />Print</Button>
          </div>
        </div>

        {memo.status === 'Archived' && (
          <Alert className="border-blue-500 bg-blue-50 text-blue-900"><Info className="h-5 w-5" /><AlertTitle>Archived</AlertTitle><AlertDescription>This memo is archived and is for reference only. Reason: {memo.archivedReason || 'N/A'}</AlertDescription></Alert>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          <div className="lg:col-span-3 space-y-6">
            <Card><CardHeader><CardTitle>Memo Content</CardTitle></CardHeader><CardContent><div className="prose max-w-none whitespace-pre-wrap">{memo.content}</div></CardContent></Card>

            {/* --- NEW/UPDATED ATTACHMENTS SECTION --- */}
            {(imageAttachments.length > 0 || otherAttachments.length > 0) && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <ImageIcon className="h-5 w-5" />
                    Attachments ({memo.attachments?.length || 0})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {/* Image attachments grid */}
                  {imageAttachments.length > 0 && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                      {imageAttachments.map((attachment) => (
                        <div key={attachment.id} className="border rounded-lg overflow-hidden group">
                          <button 
                            className="w-full text-left focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                            onClick={() => {
                              setSelectedImage(signedUrls[attachment.id]);
                              setSelectedImageAlt(attachment.caption || attachment.filename);
                            }}
                          >
                            <div className="aspect-w-16 aspect-h-9 bg-gray-100 flex items-center justify-center">
                              {signedUrls[attachment.id] ? (
                                <img 
                                  src={signedUrls[attachment.id]} 
                                  alt={attachment.caption || attachment.filename} 
                                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" 
                                />
                              ) : (
                                <div className="flex items-center justify-center h-full">
                                  <ImageIcon className="h-8 w-8 text-gray-400 animate-pulse" />
                                </div>
                              )}
                            </div>
                            <div className="p-3">
                              <p className="text-sm font-medium truncate" title={attachment.filename}>{attachment.filename}</p>
                              {attachment.caption && <p className="text-xs text-muted-foreground italic truncate" title={sanitizeCaption(attachment.caption)}>{sanitizeCaption(attachment.caption)}</p>}
                            </div>
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Divider if both types of attachments exist */}
                  {imageAttachments.length > 0 && otherAttachments.length > 0 && (
                    <div className="relative my-6">
                      <div className="absolute inset-0 flex items-center" aria-hidden="true">
                        <div className="w-full border-t border-gray-300" />
                      </div>
                      <div className="relative flex justify-center">
                        <span className="bg-white px-2 text-sm text-gray-500">Other Files</span>
                      </div>
                    </div>
                  )}

                  {/* Other file attachments list */}
                  {otherAttachments.length > 0 && (
                    <div className="space-y-3">
                      {otherAttachments.map((attachment) => (
                        <div key={attachment.id} className="border rounded-lg p-3 flex items-center gap-3">
                          <FileText className="h-8 w-8 text-muted-foreground flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="font-medium truncate" title={attachment.filename}>{attachment.filename}</p>
                            <p className="text-sm text-muted-foreground">{(attachment.size / (1024 * 1024)).toFixed(2)} MB</p>
                            {attachment.caption && <p className="text-xs text-gray-600 italic mt-1 truncate" title={sanitizeCaption(attachment.caption)}>{sanitizeCaption(attachment.caption)}</p>}
                          </div>
                          {/* Add a download button if needed */}
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
            {/* --- END OF ATTACHMENTS SECTION --- */}

            {memo.requireSignature ? (
              <Card>
                <CardHeader><CardTitle className="flex items-center gap-2"><PenTool className="h-5 w-5" />Digital Signature</CardTitle></CardHeader>
                <CardContent>
                  {isSigned ? (
                    <div className="space-y-4"><div className="flex items-center gap-2 text-green-600"><Check className="h-5 w-5" /><span>You have successfully signed this memo.</span></div><div className="bg-green-50 border border-green-200 rounded-lg p-4"><div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm"><div><strong>Signed by:</strong> {assignment?.user?.name}</div><div><strong>Date:</strong> {signature && toSydneyDate(signature.signedDate || "")}</div></div>{signature?.signatureType === 'Draw' && signature.signatureData && <div className="mt-4"><strong>Signature:</strong><div className="mt-2 border rounded p-2 bg-white"><img src={signature.signatureData} alt="Signature" className="max-h-20" /></div></div>}{signature?.signatureType === 'Type' && signature.signatureData && <div className="mt-4"><strong>Signature:</strong><div className="mt-2 border rounded p-2 bg-white font-signature text-2xl">{signature.signatureData}</div></div>}</div></div>
                  ) : (
                    <div className="space-y-4"><p className="text-sm text-muted-foreground">By signing, you acknowledge that you have read, understood, and agree to comply with this memo.</p><Dialog open={showSignatureDialog} onOpenChange={setShowSignatureDialog}><DialogTrigger asChild><Button><Signature className="h-4 w-4 mr-2" />Sign Memo</Button></DialogTrigger><DialogContent className="sm:max-w-md"><DialogHeader><DialogTitle>Sign Memo</DialogTitle></DialogHeader><div className="space-y-4"><div className="flex gap-2"><Button variant={signatureType === 'Type' ? 'default' : 'outline'} onClick={() => setSignatureType('Type')} className="flex-1">Type</Button><Button variant={signatureType === 'Draw' ? 'default' : 'outline'} onClick={() => setSignatureType('Draw')} className="flex-1">Draw</Button></div>{signatureType === 'Type' ? (<div className="space-y-2"><label className="text-sm font-medium">Enter your full name:</label><Input value={typedSignature} onChange={(e) => setTypedSignature(e.target.value)} placeholder="Your Full Name" className="font-signature text-lg" /></div>) : (<div className="space-y-2"><label className="text-sm font-medium">Draw your signature:</label><canvas ref={canvasRef} width={400} height={150} className="w-full border rounded-lg cursor-crosshair bg-gray-50" onMouseDown={startDrawing} onMouseMove={draw} onMouseUp={stopDrawing} onMouseLeave={stopDrawing} onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} onTouchEnd={stopDrawing} /><Button variant="outline" size="sm" onClick={clearCanvas}>Clear</Button></div>)}<div className="flex justify-end gap-2 pt-4"><Button variant="outline" onClick={() => setShowSignatureDialog(false)}>Cancel</Button><Button onClick={handleSignature} disabled={signatureType === 'Type' && !typedSignature.trim()}>Confirm Signature</Button></div></div></DialogContent></Dialog></div>
                  )}
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardHeader><CardTitle className="flex items-center gap-2"><Eye className="h-5 w-5" />Acknowledgment</CardTitle></CardHeader>
                <CardContent>
                  {isRead ? (
                    <div className="space-y-4"><div className="flex items-center gap-2 text-green-600"><Check className="h-5 w-5" /><span>You have acknowledged this memo.</span></div><div className="bg-green-50 border border-green-200 rounded-lg p-4"><div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm"><div><strong>Acknowledged by:</strong> {currentUser.name}</div><div><strong>Date:</strong> {toSydneyDate(assignment?.readDate || "")}</div></div></div></div>
                  ) : (
                    <div className="space-y-4"><p className="text-sm text-muted-foreground">By clicking below, you acknowledge that you have reviewed and understood this memo.</p><Button onClick={handleMarkAsRead} disabled={isMarkingRead}>{isMarkingRead ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Check className="h-4 w-4 mr-2" />}Mark as Read</Button></div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>

          <div className="space-y-6">
            <Card><CardHeader><CardTitle>Memo Information</CardTitle></CardHeader><CardContent className="space-y-4 text-sm"><div className="flex justify-between"><span>Effective Date</span><strong>{toSydneyDate(memo.effectiveDate)}</strong></div>{memo.expiryDate && <div className="flex justify-between"><span>Expiry Date</span><strong>{toSydneyDate(memo.expiryDate)}</strong></div>}<div className="flex justify-between"><span>Version</span><strong>{memo.version}</strong></div><div className="flex justify-between items-center"><span>Status</span><Badge variant="secondary" className={getStatusBadgeColor(getAssignmentStatus())}>{getAssignmentStatus()}</Badge></div><div className="flex justify-between"><span>Created By</span><strong>{typeof memo.createdBy === 'object' && memo.createdBy ? (memo.createdBy as any).name : getUserName(memo.createdBy as string)}</strong></div><div className="flex justify-between"><span>Last Updated</span><strong>{toSydneyDate(memo.updatedAt)}</strong></div></CardContent></Card>
            <Card><CardHeader><CardTitle>My Activity</CardTitle></CardHeader><CardContent className="space-y-3 text-sm">{assignment?.readDate && <div className="flex items-center gap-2"><Eye className="h-4 w-4 text-blue-500" /><span>Read on {toSydneyDate(assignment.readDate)}</span></div>}{signature?.signedDate && <div className="flex items-center gap-2"><Check className="h-4 w-4 text-green-500" /><span>Signed on {toSydneyDate(signature.signedDate)}</span></div>}{!assignment?.readDate && !signature?.signedDate && <p className="text-muted-foreground">No activity yet.</p>}</CardContent></Card>
          </div>
        </div>
      </div>
    </>
  );
}
